import React, { useState, useEffect } from "react";
import {
  FileText,
  ClipboardList,
  CheckCircle,
  DollarSign,
  Users,
  AlertCircle,
} from "lucide-react";
import useAuth from "../../../contexts/AuthContext";

interface StatCardProps {
  icon: React.ReactNode;
  title: string;
  value: number | string;
  color:
    | "blue"
    | "gray"
    | "purple"
    | "green"
    | "orange"
    | "indigo"
    | "yellow"
    | "cyan"
    | "emerald"
    | "rose"
    | "teal";
}

const StatCard = ({ icon, title, value, color }: StatCardProps) => {
  const colorClasses = {
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    gray: "bg-gray-50 text-gray-700 border-gray-200",
    purple: "bg-purple-50 text-purple-700 border-purple-200",
    green: "bg-green-50 text-green-700 border-green-200",
    orange: "bg-orange-50 text-orange-700 border-orange-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    yellow: "bg-yellow-50 text-yellow-700 border-yellow-200",
    cyan: "bg-cyan-50 text-cyan-700 border-cyan-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    teal: "bg-teal-50 text-teal-700 border-teal-200",
  };

  return (
    <div
      className={`${colorClasses[color]} rounded-lg border p-2.5 transition-all hover:shadow-md hover:scale-105`}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-xs font-medium opacity-75 mb-0.5">{title}</p>
          <p className="text-xl font-bold">{value}</p>
        </div>
        <div className="opacity-60">{icon}</div>
      </div>
    </div>
  );
};

interface SectionHeaderProps {
  icon: React.ReactNode;
  title: string;
  color: string;
}

const SectionHeader = ({ icon, title, color }: SectionHeaderProps) => (
  <div
    className={`flex items-center gap-2 mb-2.5 pb-1.5 border-b border-${color}-200`}
  >
    <div className={`text-${color}-600`}>{icon}</div>
    <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
  </div>
);

interface AlliedDashboardStatsProps {
  stats?: any;
}

export default function AlliedDashboardStats({
  stats: propStats,
}: AlliedDashboardStatsProps) {
  const [fetchedStats, setFetchedStats] = useState<any>(null);
  const { apiRequest } = useAuth();

  useEffect(() => {
    if (propStats === undefined && !fetchedStats) {
      const fetchData = async () => {
        try {
          const data = await apiRequest("/applications/dashboard-stats");
          setFetchedStats(data);
        } catch (err) {
          console.error("Failed to fetch dashboard stats:", err);
        }
      };
      fetchData();
    }
  }, [propStats, fetchedStats, apiRequest]);

  const stats = propStats || fetchedStats;
  if (!stats) return null;

  const getCount = (obj: any, key: string, fallbackKey?: string) => {
    if (!obj) return 0;
    return obj[key] ?? (fallbackKey ? obj[fallbackKey] : 0) ?? 0;
  };

  const categories = stats.categories || stats.categoryOfCandidate || {
    UR: 0,
    OBC: 0,
    SC: 0,
    ST: 0,
    EWS: 0,
  };

  return (
    <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-4 rounded-xl shadow-xs border border-gray-200">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Applications Overview */}
          <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-sm border border-gray-200 p-3.5">
            <SectionHeader
              icon={<FileText className="w-4 h-4 text-blue-600" />}
              title="Applications Overview"
              color="blue"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              <StatCard
                icon={<FileText size={18} />}
                title="Total"
                value={stats?.totalApplications || 0}
                color="blue"
              />
              <StatCard
                icon={<ClipboardList size={18} />}
                title="Draft"
                value={
                  getCount(stats?.applicationStatus, "DRAFT") || 0
                }
                color="gray"
              />
              <StatCard
                icon={<ClipboardList size={18} />}
                title="Submitted"
                value={
                  getCount(stats?.applicationStatus, "SUBMITTED") || 0
                }
                color="blue"
              />
              <StatCard
                icon={<CheckCircle size={18} />}
                title="Accepted"
                value={
                  getCount(stats?.applicationStatus, "ACCEPTED") || 0
                }
                color="green"
              />
              <StatCard
                icon={<AlertCircle size={18} />}
                title="Rejected"
                value={
                  getCount(stats?.applicationStatus, "REJECTED") || 0
                }
                color="rose"
              />
            </div>
          </div>

          {/* Payment Summary */}
          <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-sm border border-gray-200 p-3.5">
            <SectionHeader
              icon={<DollarSign className="w-4 h-4 text-green-600" />}
              title="Payment Summary"
              color="green"
            />
            <div className="grid grid-cols-3 gap-2.5">
              <StatCard
                icon={<DollarSign size={18} />}
                title="Paid"
                value={
                  getCount(stats?.paymentStatus, "PAID") || 0
                }
                color="green"
              />
              <StatCard
                icon={<CheckCircle size={18} />}
                title="Verified"
                value={
                  getCount(stats?.paymentStatus, "VERIFIED") ||
                  getCount(stats?.paymentVerification, "true") ||
                  0
                }
                color="emerald"
              />
              <StatCard
                icon={<DollarSign size={18} />}
                title="Pending"
                value={
                  getCount(stats?.paymentStatus, "PENDING") || 0
                }
                color="orange"
              />
            </div>
          </div>

          {/* Candidate Details */}
          <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-sm border border-gray-200 p-3.5">
            <SectionHeader
              icon={<Users className="w-4 h-4 text-indigo-600" />}
              title="Candidate Details"
              color="indigo"
            />
            <div className="grid grid-cols-3 gap-2.5">
              <StatCard
                icon={<Users size={18} />}
                title="Male"
                value={
                  getCount(stats?.genders, "M") ||
                  getCount(stats?.genders, "Male") ||
                  getCount(stats?.genders, "MALE") ||
                  0
                }
                color="indigo"
              />
              <StatCard
                icon={<Users size={18} />}
                title="Female"
                value={
                  getCount(stats?.genders, "F") ||
                  getCount(stats?.genders, "Female") ||
                  getCount(stats?.genders, "FEMALE") ||
                  0
                }
                color="rose"
              />
              <StatCard
                icon={<AlertCircle size={18} />}
                title="PwD"
                value={
                  getCount(stats?.pwd, "yes") ||
                  getCount(stats?.pwd, "true") ||
                  0
                }
                color="yellow"
              />
            </div>
          </div>

          {/* Category Breakdown */}
          <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-sm border border-gray-200 p-3.5">
            <SectionHeader
              icon={<ClipboardList className="w-4 h-4 text-teal-600" />}
              title="Category Breakdown"
              color="teal"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {Object.entries(categories).map(([key, value]) => (
                <StatCard
                  key={key}
                  icon={<Users size={18} />}
                  title={key}
                  value={value as any}
                  color="teal"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
